import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { RequestBodyService } from '../../../services/request-body.service';
import { JobDepartmentCategoryData } from '../../../../results/sidebar/services/job-department-category-data.service';
import { cloneDeep } from 'lodash-es';
import { TreeviewComponent, TreeviewConfig, TreeviewItem } from '../../tree-view';
import { JOB_LABELS } from '../../../services/job_labels.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
    selector: 'app-job-department',
    templateUrl: './job-department.component.html',
    styleUrls: ['./job-department.component.scss'],
    standalone: false
})
export class JobDepartmentComponent implements OnInit, OnChanges, OnDestroy {

    @Input() data: any;
    @Input() tab: any;
    @Input() disabled: boolean = false;
    @Output() jobDepartmentIsChanging = new EventEmitter<boolean>();
    @Output() jobDepartmentSelection = new EventEmitter<boolean>();

    jobDepartmentList;
    @ViewChild('treeviewComponent') treeviewComponent: TreeviewComponent;
    @ViewChild(TreeviewComponent, { read: ElementRef, static: false }) treeviewElementRef: ElementRef | undefined;
    @ViewChild('itemTemplate') itemTemplate;
    mJobDepartments = JOB_LABELS.departments;
    allItem: TreeviewItem;
    mTreeParents = [];
    isOnFilterChangeInProgress: Boolean = false;
    mutationObserver: MutationObserver;
    jobDepartmentConfig = TreeviewConfig.create(
        {
            hasAllCheckBox: false,
            hasFilter: true,
            hasCollapseExpand: false,
            decoupleChildFromParent: true,
            label: 'Enter Job Department',
            notextFound: 'No Job Department Found'
        }
    );
    parentChecked = false;

    constructor(private requestBodyService: RequestBodyService) { }

    ngOnInit() {
        if (!this.jobDepartmentList)
            this.jobDepartmentList = cloneDeep(JobDepartmentCategoryData);
        this.requestBodyService.requestContactsBody.pipe(untilDestroyed(this)).subscribe(res => {
            this.onRequestBodyChange(res);
        });
        this.requestBodyService.requestCompaniesBody.pipe(untilDestroyed(this)).subscribe(res => {
            this.onRequestBodyChange(res);
        });
    }

    onRequestBodyChange(res) {

        this.treeviewComponent.onClear();
        if (res && res['job_departments']) {
            for (const key in res['job_departments']) {
                this.data.job_departments[key] = res['job_departments'][key];
            }
            if (res['job_departments']['other'] === true) {
                this.jobDepartmentList.forEach((item) => {
                    if (item.text === 'Cross Functional') {
                        item.checked = true;
                    }
                });
            }
        }
        if (res && res['job_sub_departments']) { //saved search
            this.setJobSubDepartment(this.data.job_sub_departments);
            this.setJobDepartment();
        }
    }

    ngOnChanges() {
        if (!this.jobDepartmentList)
            this.jobDepartmentList = cloneDeep(JobDepartmentCategoryData);
        if (this.jobDepartmentList) {
            this.setJobSubDepartment(this.data.job_sub_departments)
            this.setJobDepartment();
        }
    }

    getAllParents() {
        this.allItem = new TreeviewItem({ text: 'All', value: undefined });
        this.jobDepartmentList.find((item) => {
            this.mTreeParents.find((p) => {
                if (item.text === p) {
                    item.setCollapsedRecursive(this.allItem.collapsed);
                }
            });
        });
    }

    uncheckAll() {
        this.jobDepartmentList.forEach((item) => {
            item['internalChildren']?.forEach((element) => element.checked = false);
            item.collapsed = true;
        });
    }

    setJobSubDepartment(jobSubDepartmentData: any) {
        //setting job sub department
        //Loop to create Department Tree
        let departmentTree = new Map();
        jobSubDepartmentData?.forEach((parent) => {
            let departmentItems = parent.split('~~');
            if (departmentTree.has(departmentItems[0])) {
                let childrenArray = departmentTree.get(departmentItems[0]);
                childrenArray.push(departmentItems[1]);
                departmentTree.set(departmentItems[0], childrenArray);
            } else {
                let childrenArray = [];
                childrenArray.push(departmentItems[1]);
                departmentTree.set(departmentItems[0], childrenArray);
            }
        });

        // clearing the sub-department tree
        // setting Job-sub-department from the Department tree.
        this.uncheckAll();
        departmentTree.forEach((values, key) => {
            let department = this.jobDepartmentList.find((parent) => parent.text == key);
            department.collapsed = true;
            let selectedCount = 0;
            department['internalChildren']?.forEach((child) => {
                values.forEach((v) => {
                    if (child.text == v) {
                        child.checked = true;
                        department.collapsed = false;
                        selectedCount++;
                    }
                });


            });
            if (department.collapsed === false && department['internalChildren']?.length === selectedCount) {
                department.collapsed = true;
            }
        });
    }

    setJobDepartment() {
        // setting Job Department.
        // clearing department tree and setting up
        this.jobDepartmentList.forEach((parent) => {
            parent.checked = false;
            const key = this.getkeyFromValue(parent.text);
            if (this.data.job_departments?.[key] === true) {
                parent.checked = true;
            }
        })
    }

    private findItem(item: TreeviewItem): TreeviewItem {
        if (!item) {
            return undefined;
        }
        for (const parent of this.jobDepartmentList) {
            if (parent.text === item.text) {
                return parent;
            }
            for (const child of parent.children) {
                if (child.text === item.text && parent.children.includes(item)) {
                    return child;
                }
            }
        }
    }




    ngAfterViewInit() {
        if (this.treeviewElementRef) {
            // workaround to ensure that the checkboxes remain checked without showing as unchecked for a second when we modify the text in the search box
            // if we set checkboxes back to checked natively via accessing the TreeviewItem, there is a delay of around 0.5-1 second where the checkboxes remain unchecked.
            // in our case we need to be able to set sub_department checkboxes independently of the department checkboxes
            this.mutationObserver = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type !== 'childList') {
                        return;
                    }
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeName === 'NGX-TREEVIEW-ITEM') {
                            const inputNode = (node as HTMLElement).querySelector('.row-item input') as HTMLInputElement;
                            const label = inputNode.nextElementSibling as HTMLElement | null;
                            if (label) {
                                const originalChecked = inputNode.checked;
                                const newChecked = this.data.job_departments[this.getkeyFromValue(label.innerText.trim())];
                                if (newChecked === undefined) {
                                    return;
                                }
                                if (originalChecked !== newChecked) {
                                    inputNode.checked = newChecked;
                                }
                            }
                        }
                    });
                });
            });
            this.mutationObserver.observe(this.treeviewElementRef.nativeElement, { childList: true, subtree: true });
        }
    }

    selectedChange() {
        this.data.job_sub_departments = [];
        this.jobDepartmentList.forEach((parent) => {
            const key = this.getkeyFromValue(parent.text);
            if (!this.data?.job_departments){
                this.data['job_departments'] = {};
            }
            this.data.job_departments[key] = parent.checked;
            parent['children']?.forEach(child => {
                if (child.checked) {
                    this.data.job_sub_departments.push(parent.text + '~~' + child.text);
                }
            });
        });
    }

    getkeyFromValue(value) {
        return Object.keys(this.mJobDepartments).find(key => this.mJobDepartments[key] === value);
    }

    filterChange(text: string) {
        this.treeviewComponent.onFilterTextChange(text);
    }

    jobFilterChangeAction(item: TreeviewItem) {
        const foundItem = this.findItem(item);
        if (foundItem) {
            foundItem.checked = item.checked;
        }
        if (!item.children && item.text !== 'Cross Functional') {
            this.setParentCheckboxesToSavedData();
            this.selectedChange();
            this.requestBodyService.universalFilterChanged.next(this.data);
        } else {
            this.selectedChange();
            this.requestBodyService.universalFilterChanged.next(this.data);
        }
        this.jobDepartmentSelection.emit({...this.data});
    }

    jobFilterChange(item: TreeviewItem) {
        if (this.isOnFilterChangeInProgress) {
            this.jobDepartmentIsChanging.emit(true);
            setTimeout(() => {
                this.jobFilterChangeAction(item);
                this.jobDepartmentIsChanging.emit(false);
            }, 0);
        } else {
            this.jobFilterChangeAction(item);
        }
    }

    onFilterChange(event) {
        this.isOnFilterChangeInProgress = true;
        setTimeout(() => {
            this.setParentCheckboxesToSavedData();
            if (event === '') {
                this.expandIfSubDepartmentChecked();
            }
            this.isOnFilterChangeInProgress = false;
        }, 0);
    }

    private expandIfSubDepartmentChecked() {
        this.jobDepartmentList.forEach((parent) => {
            if (parent.children?.length) {
                for (const child of parent.children) {
                    if (child.checked) {
                        parent.collapsed = false;
                        break;
                    }
                }
            }
        });
    }

    private setParentCheckboxesToSavedData() {
        this.jobDepartmentList.forEach((parent) => {
            const key = this.getkeyFromValue(parent.text);
            parent.checked = this.data.job_departments[key];
            parent.internalChecked = this.data.job_departments[key];
        });
    }

    ngOnDestroy() {
        this.mutationObserver.disconnect();
    }
}
